import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LaunchDarklyFlag, LaunchDarklyService } from '@anduin/auth';
import { ConfigService, Globals, SET_ME } from '@anduin/config';
import { Logger, ScriptLoaderService } from '@anduin/shared';
import { interval } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import {
  ROUTE_TOKEN_ACCOUNTS_RECEIVABLE,
  ROUTE_TOKEN_BILLING_BASE,
  ROUTE_TOKEN_CLIENTS_AND_CONTACTS,
  ROUTE_TOKEN_CLIENT_MANAGEMENT,
  ROUTE_TOKEN_COLLECTIONS_BASE,
  ROUTE_TOKEN_ENGAGEMENTS,
  ROUTE_TOKEN_HOME,
  ROUTE_TOKEN_INVOICES_BASE,
  ROUTE_TOKEN_PAYMENTS,
  ROUTE_TOKEN_PRACTICE_OS,
  ROUTE_TOKEN_PROJECTS_BASE,
  ROUTE_TOKEN_SETTINGS,
} from '../routing-constants';
import { ChatbotApiService } from './chatbot-api.service';

type ZendeskGlobal = ((...args: unknown[]) => void) | undefined;

@Injectable({
  providedIn: 'root',
})
export class ZendeskWidgetService {
  // One Zendesk instance serves every firm-portal environment, so the Firm Support
  // brand's widget key and the custom ticket field ids are not per-environment config.
  private static readonly WIDGET_SRC = 'https://static.zdassets.com/ekr/snippet.js?key=f4d91e71-35fe-486c-aba1-cf120026c1c3';

  // The messenger cannot re-authenticate mid-session: at token expiry it silently drops
  // the conversation to anonymous even when the refresh callback supplies a fresh token.
  // Re-running the full loginUser inside the 1h TTL is what keeps a long-lived tab signed in.
  private static readonly RELOGIN_INTERVAL_MS = 30 * 60 * 1000;

  private readonly fieldIds = {
    tenantId: '51589270110619',
    portalType: '51589297691931',
    featureContext: '51589273327259',
    firmName: '53599210627355',
    pmSystem: '53599465875099',
  };

  private readonly featureAreas: readonly string[] = [
    ROUTE_TOKEN_HOME,
    ROUTE_TOKEN_BILLING_BASE,
    ROUTE_TOKEN_PROJECTS_BASE,
    ROUTE_TOKEN_ENGAGEMENTS,
    ROUTE_TOKEN_SETTINGS,
    ROUTE_TOKEN_PAYMENTS,
    ROUTE_TOKEN_ACCOUNTS_RECEIVABLE,
    ROUTE_TOKEN_COLLECTIONS_BASE,
    ROUTE_TOKEN_INVOICES_BASE,
    ROUTE_TOKEN_PRACTICE_OS,
    ROUTE_TOKEN_CLIENT_MANAGEMENT,
    ROUTE_TOKEN_CLIENTS_AND_CONTACTS,
  ];

  private readonly _class = 'ZendeskWidgetService';

  private readonly configService = inject(ConfigService);
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly globals = inject(Globals);
  private readonly router = inject(Router);
  private readonly logger = inject(Logger);
  private readonly chatbotApi = inject(ChatbotApiService);
  private readonly launchDarkly = inject(LaunchDarklyService);

  initialize(): void {
    this.launchDarkly
      .getFeatureFlag(LaunchDarklyFlag.ENABLE_FIRM_PORTAL_CHATBOT)
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() =>
          this.scriptLoader.loadScript(ZendeskWidgetService.WIDGET_SRC, false, [{ attribute: 'id', value: 'ze-snippet' }]),
        ),
        // loadScript reports onerror as next(false) and re-emits whenever any other
        // script's status changes; only the first successful load should start us.
        filter((didLoad) => {
          if (!didLoad) {
            this.logger.error(`${this._class} :: initialize :: chat script failed to load`);
          }
          return didLoad;
        }),
        take(1),
      )
      .subscribe(() => this.start());
  }

  /**
   * Opens the messenger window from an explicit entry point (the Help menu item).
   * The launcher suppression is lifted while the window is open because the launcher doubles
   * as the messenger's only close control on desktop.
   */
  openMessenger(): void {
    const zE = this.zendesk();
    if (!zE) {
      return;
    }
    try {
      this.setLauncherSuppressed(false);
      zE('messenger', 'show');
      zE('messenger', 'open');
    } catch (error) {
      this.logger.error(`${this._class} :: openMessenger :: zE threw`, error);
    }
  }

  private start(): void {
    this.suppressDefaultLauncher();

    this.launchDarkly
      .getFeatureFlag(LaunchDarklyFlag.ENABLE_FIRM_CHATBOT_IDENTITY)
      .pipe(take(1), filter(Boolean))
      .subscribe(() => {
        this.authenticate();
        interval(ZendeskWidgetService.RELOGIN_INTERVAL_MS).subscribe(() => this.authenticate());
      });

    this.setConversationFields();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.setConversationFields());
  }

  /**
   * The widget only surfaces through the portal's own entry points. zE('messenger','hide')
   * stops working once the launcher has been shown (Zendesk re-shows it after every
   * open/close cycle), so suppression is enforced with a parent-page stylesheet on the
   * launcher iframe instead, lifted only while the messenger window is open.
   */
  private suppressDefaultLauncher(): void {
    // The proactive-message teaser ("Message from company") and its round dismiss button
    // ("Close message") are separate iframes that float over the sidenav and steal its
    // clicks, so unlike the launcher they stay suppressed even while open.
    const teaserStyleId = 'zendesk-teaser-suppression';
    if (!document.getElementById(teaserStyleId)) {
      const style = document.createElement('style');
      style.id = teaserStyleId;
      style.textContent = 'iframe[title="Message from company"], iframe[title="Close message"] { display: none !important; }';
      document.head.appendChild(style);
    }

    this.setLauncherSuppressed(true);
    const zE = this.zendesk();
    if (!zE) {
      return;
    }
    try {
      zE('messenger:on', 'close', () => this.setLauncherSuppressed(true));
    } catch (error) {
      this.logger.error(`${this._class} :: suppressDefaultLauncher :: zE threw`, error);
    }
  }

  private setLauncherSuppressed(suppressed: boolean): void {
    const styleId = 'zendesk-launcher-suppression';
    const existing = document.getElementById(styleId);
    if (suppressed && !existing) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = 'iframe#launcher { display: none !important; }';
      document.head.appendChild(style);
    } else if (!suppressed && existing) {
      existing.remove();
    }
  }

  /**
   * Zendesk re-invokes the loginUser callback whenever it needs a fresh token. Replaying
   * the first one would de-authenticate the session at expiry (messages stall in
   * "Sending"), so only the first invocation reuses the already-minted token.
   */
  private authenticate(): void {
    this.chatbotApi.mintMessagingJwt().subscribe({
      next: ({ jwt }) => {
        if (!jwt) {
          this.logger.info(`${this._class} :: authenticate :: no jwt; staying anonymous`);
          return;
        }
        const zE = this.zendesk();
        if (!zE) {
          return;
        }
        let initialJwt: string | null = jwt;
        try {
          zE('messenger', 'loginUser', (callback: (token: string) => void) => {
            if (initialJwt) {
              const token = initialJwt;
              initialJwt = null;
              callback(token);
              return;
            }
            this.chatbotApi.mintMessagingJwt().subscribe({
              next: ({ jwt: freshJwt }) => {
                if (!freshJwt) {
                  this.logger.info(`${this._class} :: authenticate :: refresh mint returned no jwt; session de-authenticates`);
                  return;
                }
                callback(freshJwt);
              },
              error: (error) => this.logger.error(`${this._class} :: authenticate :: refresh mint failed`, error),
            });
          });
          this.logger.info(`${this._class} :: authenticate :: messenger loginUser invoked`);
        } catch (error) {
          this.logger.error(`${this._class} :: authenticate :: zE loginUser threw`, error);
        }
      },
      error: (error) => this.logger.error(`${this._class} :: authenticate :: mint failed; staying anonymous`, error),
    });
  }

  private setConversationFields(): void {
    const zE = this.zendesk();
    if (!zE) {
      return;
    }

    try {
      // Zendesk drops conversationFields whose value is empty, so firm-context fields
      // are only included when the config actually carries a value.
      const fields: { id: string; value: string }[] = [
        { id: this.fieldIds.tenantId, value: this.globals.anTenant },
        { id: this.fieldIds.portalType, value: 'firm_portal' },
        { id: this.fieldIds.featureContext, value: this.currentFeatureArea() },
      ];
      const firmName = this.configuredValue('firmName', this.configService.customerName);
      if (firmName) {
        fields.push({ id: this.fieldIds.firmName, value: firmName });
      }
      const pmSystem = this.configuredValue('pmSystem', this.configService.pmSystem);
      if (pmSystem) {
        fields.push({ id: this.fieldIds.pmSystem, value: pmSystem });
      }

      zE('messenger:set', 'conversationFields', fields);
    } catch (error) {
      this.logger.error(`${this._class} :: setConversationFields :: zE threw`, error);
    }
  }

  private zendesk(): (...args: unknown[]) => void {
    const zE = (window as { zE?: ZendeskGlobal }).zE;
    if (!zE) {
      this.logger.warn(`${this._class} :: zendesk :: zE not present after script load`);
    }
    return zE as (...args: unknown[]) => void;
  }

  // A mid-onboarding firm must not file tickets reading SET-ME.
  private configuredValue(fieldName: string, value: string | undefined): string | undefined {
    if (!value || value === SET_ME) {
      this.logger.debug(`${this._class} :: setConversationFields :: omitting unconfigured ${fieldName}`);
      return undefined;
    }
    return value;
  }

  // Coarse feature area from the current URL — never the raw URL, which can carry identifiers.
  private currentFeatureArea(): string {
    const segments = this.router.url
      .split(/[?#;]/)[0]
      .split('/')
      .filter((segment) => segment.length > 0);
    const first = segments[0] ?? '';
    return this.featureAreas.includes(first) ? first : 'other';
  }
}
